import React, { useState, useMemo, useEffect, useRef } from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  FormText,
  FormFeedback,
  Button,
} from 'reactstrap';

import JiraFieldCombobox from './JiraFieldCombobox';

const lower = (s) => (s == null ? '' : String(s).toLowerCase());

/** Sprint: schema type "array", items "json", custom "com.pyxis.greenhopper.jira:gh-sprint" */
function isPossibleSprintField(f) {
  const schema = f.schema || {};
  const type = schema.type || '';
  const items = schema.items;
  const itemsOk =
    items === 'json' ||
    (items && typeof items === 'object' && items.type === 'json');
  const custom = lower(schema.custom || '');
  return (
    type === 'array' &&
    itemsOk &&
    custom.includes('com.pyxis.greenhopper.jira:gh-sprint')
  );
}

/** Story points: possible if schema type "number" (no name requirement). */
function isPossibleStoryPointsField(f) {
  const schema = f.schema || {};
  const type = (schema.type || '').toLowerCase();
  return type === 'number';
}

/**
 * Story points match score. MUST be type === 'number'. Higher = better.
 * Multiple criteria stack for more nuanced tiers:
 * - 3: default id AND (custom jsw OR name "story points") - meets all criteria
 * - 2.5: default id OR custom jsw (strong indicator alone)
 * - 2: name includes "Story Points" / "story point" (e.g. Adjusted Story Points, Story Points)
 * - 1: number type only (e.g. Github ID, Github Number)
 * - 0.5: fallback
 */
function scoreStoryPointsField(f) {
  const schema = f.schema || {};
  if ((schema.type || '').toLowerCase() !== 'number') return 0;
  const id = (f.id || '').toLowerCase();
  const custom = lower(schema.custom || '');
  const displayName = lower(f.untranslatedName || f.name || '');
  const hasDefaultId = id === 'customfield_10016';
  const hasJswCustom = custom.includes(
    'com.pyxis.greenhopper.jira:jsw-story-points'
  );
  const hasStoryPointsName =
    displayName.includes('story points') || displayName.includes('story point');

  if (hasDefaultId && (hasJswCustom || hasStoryPointsName)) return 3;
  if (hasDefaultId || hasJswCustom) return 2.5;
  if (hasStoryPointsName) return 2;
  return 1;
}

/** Hours: possible if type "number" and (known time id or name suggests estimate). */
function isPossibleHoursField(f) {
  const schema = f.schema || {};
  const type = (schema.type || '').toLowerCase();
  if (type !== 'number') return false;
  const id = (f.id || '').toLowerCase();
  const name = lower(f.name || '');
  const knownTimeIds = [
    'timeoriginalestimate',
    'timeestimate',
    'aggregatetimeoriginalestimate',
  ];
  if (knownTimeIds.includes(id)) return true;
  if (name.includes('estimate') || name.includes('time')) return true;
  return false;
}

/**
 * Hours match score. MUST be type === 'number". Higher = better.
 * - 3: timeoriginalestimate AND name suggests time estimate
 * - 2.5: id === "timeoriginalestimate"
 * - 2: id timeestimate or aggregatetimeoriginalestimate
 * - 1: name includes "estimate" and not "story"/"point"
 * - 0.5: name includes "estimate" but also "story"/"point", or other
 */
function scoreHoursField(f) {
  const schema = f.schema || {};
  if ((schema.type || '').toLowerCase() !== 'number') return 0;
  const id = (f.id || '').toLowerCase();
  const name = lower(f.name || '');
  const hasEstimateName =
    name.includes('estimate') &&
    !name.includes('story') &&
    !name.includes('point');
  const hasStoryPointInName = name.includes('story') || name.includes('point');

  if (
    id === 'timeoriginalestimate' &&
    (name.includes('original') || name.includes('estimate'))
  )
    return 3;
  if (id === 'timeoriginalestimate') return 2.5;
  if (id === 'timeestimate' || id === 'aggregatetimeoriginalestimate') return 2;
  if (hasEstimateName) return 1;
  if (hasStoryPointInName && name.includes('estimate')) return 0.5;
  return 0.5;
}

function toFieldOption(f, score = null) {
  const name =
    f.untranslatedName != null
      ? f.untranslatedName
      : f.name != null
      ? f.name
      : f.id;
  const opt = {
    id: f.id,
    name,
    untranslatedName: f.untranslatedName ?? f.name ?? f.id,
  };
  if (score != null) opt.score = score;
  return opt;
}

function sortOptionsByName(opts) {
  return [...opts].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/** Sort by score desc, then by name. Items may have optional .score */
function sortOptionsByScoreThenName(opts) {
  return [...opts].sort((a, b) => {
    const sa = a.score != null ? a.score : 0;
    const sb = b.score != null ? b.score : 0;
    if (sb !== sa) return sb - sa;
    return (a.name || '').localeCompare(b.name || '', undefined, {
      sensitivity: 'base',
    });
  });
}

/**
 * Find best-matching field IDs using scoring among possible fields.
 * Sprint: first possible (schema match). Story points / Hours: max score among possible.
 */
function findBestMatchFieldIds(fields) {
  if (!Array.isArray(fields) || fields.length === 0) return {};
  const sprint = fields.find(isPossibleSprintField);
  const storyPointsPossible = fields.filter(isPossibleStoryPointsField);
  const storyPoints =
    storyPointsPossible.length > 0
      ? storyPointsPossible.reduce((best, f) =>
          scoreStoryPointsField(f) > scoreStoryPointsField(best) ? f : best
        )
      : null;
  const hoursPossible = fields.filter(isPossibleHoursField);
  const hours =
    hoursPossible.length > 0
      ? hoursPossible.reduce((best, f) =>
          scoreHoursField(f) > scoreHoursField(best) ? f : best
        )
      : null;
  return {
    ...(sprint && { sprint_field_id: sprint.id }),
    ...(storyPoints && { story_points_field_id: storyPoints.id }),
    ...(hours && { hours_field_id: hours.id }),
  };
}

// Create validation schema based on edit mode
const createValidationSchema = (isEdit) =>
  Yup.object().shape({
    name: Yup.string()
      .required('Connector name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters'),

    base_url: Yup.string()
      .required('Jira base URL is required')
      .url('Please enter a valid URL')
      .matches(
        /^https:\/\/.*\.atlassian\.net\/?$/,
        'URL must be a valid Atlassian Jira URL (e.g., https://company.atlassian.net)'
      ),

    encrypted_credentials: Yup.object().shape({
      token: isEdit
        ? Yup.string().min(10, 'API token must be at least 10 characters') // Optional for edit mode
        : Yup.string()
            .required('API token is required')
            .min(10, 'API token must be at least 10 characters'),
    }),

    configuration: Yup.object().shape({
      username: Yup.string()
        .required('Username/email is required')
        .email('Please enter a valid email address'),

      api_version: Yup.string()
        .required('API Version is required')
        .oneOf(['3'], 'API Version must be 3'),

      fields: Yup.object().shape({
        sprint: Yup.object().shape({
          id: Yup.string(),
          name: Yup.string(),
          untranslated_name: Yup.string(),
        }),
        story_points: Yup.object().shape({
          id: Yup.string(),
          name: Yup.string(),
          untranslated_name: Yup.string(),
        }),
        hours: Yup.object().shape({
          id: Yup.string(),
          name: Yup.string(),
          untranslated_name: Yup.string(),
        }),
      }),
    }),
  });

const JiraConfigurationForm = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  isSubmitting,
  isValid,
  onSubmit,
  onBack,
  onCancel,
  onTest,
  isLoading,
  isFormValid,
  isEdit = false,
  issueFields = [],
  onQueryIssueFields,
}) => {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [localIssueFields, setLocalIssueFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [fieldsError, setFieldsError] = useState(null);
  const testOnBlurTimeoutRef = useRef(null);

  const fieldsList = useMemo(
    () => (isEdit ? issueFields : localIssueFields) || [],
    [isEdit, issueFields, localIssueFields]
  );

  const sprintFieldOptions = useMemo(
    () =>
      sortOptionsByName(
        fieldsList.filter(isPossibleSprintField).map((f) => toFieldOption(f, 1))
      ),
    [fieldsList]
  );
  const storyPointsFieldOptions = useMemo(
    () =>
      sortOptionsByScoreThenName(
        fieldsList
          .filter(isPossibleStoryPointsField)
          .map((f) => toFieldOption(f, scoreStoryPointsField(f)))
      ),
    [fieldsList]
  );
  const hoursFieldOptions = useMemo(
    () =>
      sortOptionsByScoreThenName(
        fieldsList
          .filter(isPossibleHoursField)
          .map((f) => toFieldOption(f, scoreHoursField(f)))
      ),
    [fieldsList]
  );

  const fieldDisplayName = (f) => f?.untranslatedName ?? f?.name ?? f?.id ?? '';

  // When issue fields are loaded, set defaults from best match if configuration not already set
  useEffect(() => {
    if (!fieldsList.length) return;
    const best = findBestMatchFieldIds(fieldsList);
    const cfg = values.configuration || {};
    const cfgFields = cfg.fields || {};
    if (!cfgFields.sprint?.id?.trim() && best.sprint_field_id) {
      const f = fieldsList.find((x) => x.id === best.sprint_field_id);
      setFieldValue('configuration.fields.sprint', {
        id: best.sprint_field_id,
        name: fieldDisplayName(f) || '',
        untranslated_name: fieldDisplayName(f) || '',
      });
    }
    if (!cfgFields.story_points?.id?.trim() && best.story_points_field_id) {
      const f = fieldsList.find((x) => x.id === best.story_points_field_id);
      setFieldValue('configuration.fields.story_points', {
        id: best.story_points_field_id,
        name: fieldDisplayName(f) || '',
        untranslated_name: fieldDisplayName(f) || '',
      });
    }
    if (!cfgFields.hours?.id?.trim() && best.hours_field_id) {
      const f = fieldsList.find((x) => x.id === best.hours_field_id);
      setFieldValue('configuration.fields.hours', {
        id: best.hours_field_id,
        name: fieldDisplayName(f) || '',
        untranslated_name: fieldDisplayName(f) || '',
      });
    }
    // Only run when the fields list becomes available; intentionally not depending on values to avoid overwriting user edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsList.length]);

  // Create only: get issue fields once after successful test; then match list to configuration
  const loadFieldsAfterTestSuccess = () => {
    if (isEdit) return;
    if (!onQueryIssueFields) return;
    setLoadingFields(true);
    setFieldsError(null);
    onQueryIssueFields(values)
      .then((fields) =>
        setLocalIssueFields(Array.isArray(fields) ? fields : [])
      )
      .catch((err) => {
        setFieldsError(err?.message || 'Failed to load fields');
      })
      .finally(() => setLoadingFields(false));
  };

  const hasMinimumForTest = () => {
    if (!values.base_url?.trim() || !values.configuration?.username?.trim())
      return false;
    if (!isEdit && !values.encrypted_credentials?.token?.trim()) return false;
    return true;
  };

  const scheduleTestOnBlur = () => {
    if (!hasMinimumForTest()) return;
    if (testOnBlurTimeoutRef.current)
      clearTimeout(testOnBlurTimeoutRef.current);
    testOnBlurTimeoutRef.current = setTimeout(() => {
      testOnBlurTimeoutRef.current = null;
      handleTestConnection();
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (testOnBlurTimeoutRef.current)
        clearTimeout(testOnBlurTimeoutRef.current);
    };
  }, []);

  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName] || '',
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: !!(errors[fieldName] && touched[fieldName]),
  });

  const formikAttributesWithTestOnBlur = (fieldName) => ({
    ...formikAttributes(fieldName),
    onBlur: (e) => {
      handleBlur(e);
      scheduleTestOnBlur();
    },
  });

  const feedback = (fieldName) => {
    if (errors[fieldName] && touched[fieldName]) {
      return <FormFeedback>{errors[fieldName]}</FormFeedback>;
    }
    return null;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
    // The parent's onSubmit will be called by Formik's handleSubmit
  };

  const handleTestConnection = async () => {
    const hasToken = !!values.encrypted_credentials?.token?.trim();
    if (!values.base_url || !values.configuration?.username) {
      setTestResult({
        success: false,
        message: 'Please enter base URL and username before testing',
      });
      return;
    }
    if (!isEdit && !hasToken) {
      setTestResult({
        success: false,
        message: 'Please enter an API token before testing',
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      if (onTest) {
        await onTest(values);
        setTestResult({
          success: true,
          message: 'Connection test successful!',
        });
        loadFieldsAfterTestSuccess();
      }
    } catch (error) {
      console.log('💣 error', error);
      setTestResult({
        success: false,
        message:
          // error.message ||
          'Connection test failed. Please check your credentials and URL.',
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <Row>
        <Col md={12}>
          <FormGroup>
            <Label for="name">Connector Name</Label>
            <Input
              type="text"
              {...formikAttributes('name')}
              placeholder="My Jira Integration"
              className="limited-width-lg"
              required
            />
            {feedback('name')}
            <FormText color="muted">
              A friendly name to identify this connector
            </FormText>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <FormGroup>
            <Label for="base_url">Jira Base URL</Label>
            <Input
              type="url"
              {...formikAttributesWithTestOnBlur('base_url')}
              placeholder="https://your-domain.atlassian.net"
              className="limited-width-lg"
              required
            />
            {feedback('base_url')}
            <FormText color="muted">
              Your Jira instance URL (e.g., https://company.atlassian.net)
            </FormText>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <FormGroup>
            <Label for="configuration.username">Username/Email</Label>
            <Input
              type="email"
              id="configuration.username"
              name="configuration.username"
              className="limited-width-lg"
              value={values.configuration?.username || ''}
              onChange={(e) =>
                setFieldValue('configuration.username', e.target.value)
              }
              onBlur={(e) => {
                handleBlur(e);
                scheduleTestOnBlur();
              }}
              invalid={
                !!(
                  errors.configuration?.username &&
                  touched.configuration?.username
                )
              }
              placeholder="user@company.com"
              required
            />
            {errors.configuration?.username &&
              touched.configuration?.username && (
                <FormFeedback>{errors.configuration.username}</FormFeedback>
              )}
            <FormText color="muted">Your Jira account email address</FormText>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <FormGroup>
            <Label for="encrypted_credentials.token">API Token</Label>
            <Input
              type="password"
              id="encrypted_credentials.token"
              name="encrypted_credentials.token"
              value={values.encrypted_credentials?.token || ''}
              onChange={(e) =>
                setFieldValue('encrypted_credentials.token', e.target.value)
              }
              onBlur={(e) => {
                handleBlur(e);
                scheduleTestOnBlur();
              }}
              className="limited-width-lg"
              invalid={
                !!(
                  errors.encrypted_credentials?.token &&
                  touched.encrypted_credentials?.token
                )
              }
              placeholder={
                isEdit
                  ? 'Leave blank to keep existing token'
                  : 'Enter your API token'
              }
              required={!isEdit}
            />
            {errors.encrypted_credentials?.token &&
              touched.encrypted_credentials?.token && (
                <FormFeedback>
                  {errors.encrypted_credentials.token}
                </FormFeedback>
              )}
            <FormText color="muted">
              {isEdit ? (
                'Leave blank to keep the existing token, or enter a new one'
              ) : (
                <>
                  Generate an API token from your{' '}
                  <a
                    href="https://id.atlassian.com/manage-profile/security/api-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Atlassian account settings
                  </a>
                </>
              )}
            </FormText>
          </FormGroup>
        </Col>
      </Row>

      {/* Test Connection Section */}
      <Row className="mt-4">
        <Col md={12}>
          <div className="border-top pt-3">
            <h6>Test Connection</h6>
            <p className="text-muted small mb-3">
              Test your Jira connection to verify the credentials and URL are
              correct.
            </p>

            {testResult && (
              <div
                className={`alert ${
                  testResult.success ? 'alert-success' : 'alert-danger'
                } mb-3`}
              >
                {testResult.message}
              </div>
            )}

            <Button
              type="button"
              color="info"
              onClick={handleTestConnection}
              disabled={
                isTesting ||
                !values.base_url ||
                !values.configuration?.username ||
                (!isEdit && !values.encrypted_credentials?.token)
              }
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          <div className="border-top pt-3">
            <h6>Jira field mapping</h6>
            <p className="text-muted small mb-3">
              Select the Jira fields to use. Type to filter.{' '}
            </p>
            {loadingFields && (
              <p className="text-muted small mb-2">Loading issue fields...</p>
            )}
            {fieldsError && (
              <div className="text-danger small mb-2">{fieldsError}</div>
            )}
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="configuration.fields.sprint">Sprint field</Label>
                  <JiraFieldCombobox
                    id="configuration.fields.sprint"
                    placeholder="Select or type to filter..."
                    items={sprintFieldOptions}
                    selectedId={
                      values.configuration?.fields?.sprint?.id?.trim() || ''
                    }
                    selectedName={
                      values.configuration?.fields?.sprint?.name?.trim() ||
                      values.configuration?.fields?.sprint?.untranslated_name?.trim() ||
                      ''
                    }
                    onSelect={(id) => {
                      const opt = sprintFieldOptions.find((o) => o.id === id);
                      setFieldValue('configuration.fields.sprint', {
                        id: id || '',
                        name: opt?.name ?? '',
                        untranslated_name:
                          opt?.untranslatedName ?? opt?.name ?? '',
                      });
                    }}
                    disabled={!sprintFieldOptions.length}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="configuration.fields.story_points">
                    Story points field
                  </Label>
                  <JiraFieldCombobox
                    id="configuration.fields.story_points"
                    placeholder="Select or type to filter..."
                    items={storyPointsFieldOptions}
                    selectedId={
                      values.configuration?.fields?.story_points?.id?.trim() ||
                      ''
                    }
                    selectedName={
                      values.configuration?.fields?.story_points?.name?.trim() ||
                      values.configuration?.fields?.story_points?.untranslated_name?.trim() ||
                      ''
                    }
                    onSelect={(id) => {
                      const opt = storyPointsFieldOptions.find(
                        (o) => o.id === id
                      );
                      setFieldValue('configuration.fields.story_points', {
                        id: id || '',
                        name: opt?.name ?? '',
                        untranslated_name:
                          opt?.untranslatedName ?? opt?.name ?? '',
                      });
                    }}
                    disabled={!storyPointsFieldOptions.length}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="configuration.fields.hours">Hours field</Label>
                  <JiraFieldCombobox
                    id="configuration.fields.hours"
                    placeholder="Select or type to filter..."
                    items={hoursFieldOptions}
                    selectedId={
                      values.configuration?.fields?.hours?.id?.trim() || ''
                    }
                    selectedName={
                      values.configuration?.fields?.hours?.name?.trim() ||
                      values.configuration?.fields?.hours?.untranslated_name?.trim() ||
                      ''
                    }
                    onSelect={(id) => {
                      const opt = hoursFieldOptions.find((o) => o.id === id);
                      setFieldValue('configuration.fields.hours', {
                        id: id || '',
                        name: opt?.name ?? '',
                        untranslated_name:
                          opt?.untranslatedName ?? opt?.name ?? '',
                      });
                    }}
                    disabled={!hoursFieldOptions.length}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {/* Only show action buttons for create mode */}
      {isEdit ? (
        <Row className="mt-4">
          <Col className="text-right">
            <Button
              color="secondary"
              onClick={onCancel}
              disabled={isLoading || isSubmitting}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isLoading || isSubmitting || !isValid}
            >
              {isLoading || isSubmitting ? 'Saving...' : 'Update Connector'}
            </Button>
          </Col>
        </Row>
      ) : (
        <Row className="mt-4">
          <Col className="text-right">
            <Button
              color="secondary"
              onClick={onBack}
              disabled={isLoading || isSubmitting}
              className="mr-2"
            >
              Back to Service Selection
            </Button>
            <Button
              color="secondary"
              onClick={onCancel}
              disabled={isLoading || isSubmitting}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isLoading || isSubmitting || !isValid}
            >
              {isLoading || isSubmitting ? 'Creating...' : 'Create Connector'}
            </Button>
          </Col>
        </Row>
      )}
    </Form>
  );
};

// Formik HOC
const JiraConfiguration = withFormik({
  mapPropsToValues: ({ connector = {} }) => ({
    name: connector.name || '',
    base_url: connector.base_url || '',
    encrypted_credentials: {
      token: '', // Don't pre-fill sensitive data
    },
    configuration: {
      username: connector.configuration?.username || '',
      api_version: '3', // Always use API version 3
      fields: {
        sprint: connector.configuration?.fields?.sprint || {
          id: '',
          name: '',
          untranslated_name: '',
        },
        story_points: connector.configuration?.fields?.story_points || {
          id: '',
          name: '',
          untranslated_name: '',
        },
        hours: connector.configuration?.fields?.hours || {
          id: '',
          name: '',
          untranslated_name: '',
        },
      },
    },
  }),

  validationSchema: (props) => createValidationSchema(props.isEdit),

  handleSubmit: (values, { props, setSubmitting }) => {
    // Call the parent's onSubmit function with the formik values
    if (props.onSubmit) {
      props.onSubmit(values);
    }
    setSubmitting(false);
  },

  enableReinitialize: true,
})(JiraConfigurationForm);

export default JiraConfiguration;
